import { inject, Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { DottedKey } from '../../../../core/field-definitions/entities/field-definition.entity';
import { ExtendedProfileField } from '../../../../core/profiles/entities/extended-profile-field.entity';
import { ApiFragment } from '../../../../core/records/entities/api-fragment.class';
import { Cell } from '../../components/cells/_base/table-cell.entity';
import { LinkCell, LinkCellInterface } from '../../components/cells/link-cell/link-cell.entity';
import { ColumnWidth } from '../../entities/column-width.entity';
import { ProfileFieldColumnService } from '../base-columns/profile-field-column.service';
import { sessionStorageHelper } from '../../../../session-storage/helpers/fetch-from-session-storage/session-storage.helper';
import { Router } from '@angular/router';

@Injectable({ providedIn: 'root' })
export class ParentColumnService extends ProfileFieldColumnService<ApiFragment> {
    protected readonly WIDTH: ColumnWidth = ColumnWidth.NORMAL;
    protected readonly DISABLE_SORT: boolean = true; // Disabling because this calculated value can come from different sources

    private readonly translateService: TranslateService = inject(TranslateService);
    private readonly router: Router = inject(Router);

    protected getMapper(profileField: ExtendedProfileField): (item: ApiFragment) => Cell {
        return record => new LinkCell(this.getParentLinkData(record));
    }

    private getParentLinkData(fragment: ApiFragment): LinkCellInterface {
        let title = '';
        let info = '';
        let route = '';
        const mainSegment: string = sessionStorageHelper.has('shareId')
            ? `shared/${sessionStorageHelper.get('shareId')}/single`
            : 'records';

        if (fragment.hasParent()) {
            title = fragment.getDirectParentTitle() ?? '';
            route = `/${mainSegment}/${fragment.getParentRecordId()}`;
        } else if (fragment.isCandidateChild()) {
            title = this.translateService.instant('overrides.Type.parent');
            route = `/${mainSegment}/${fragment.getCandidateParentId()}`;
            info = this.translateService.instant('errors.title-not-available');
        }

        const currentRouterUrl = this.router.url;

        // Extract just the path part from the URL (without query params)
        let pageUrl = currentRouterUrl;
        if (currentRouterUrl.includes('?')) {
            pageUrl = currentRouterUrl.split('?')[0];
        }

        return {
            title,
            info,
            navigationLink: {
                route,
                extras: {
                    state: {
                        queryIteratorStack: [
                            {
                                backToLangkey: this.translateService.instant('components.pagination.back-to.search'),
                                pageUrl
                            }
                        ]
                    }
                }
            }
        };
    }

    protected getFieldSelectDottedKeys(profileField: ExtendedProfileField): DottedKey[] {
        return [
            'Structural.ReferenceCodes',
            'Structural.ParentRecordId',
            'Structural.ReferenceTitles',
            'Structural.Relations'
        ] as DottedKey[];
    }
}
